import React from 'react';
import axios from 'axios';
import i18n from '../i18n';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.ertiqasystem.com/api';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    // إذا كان خطأ تحميل chunk (بعد نشر كود جديد) — أعد تحميل الصفحة تلقائياً
    if (error?.name === 'ChunkLoadError' || error?.message?.includes('Loading chunk') || error?.message?.includes('Loading CSS chunk')) {
      window.location.reload();
      return { hasError: false, error: null };
    }
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // إرسال الخطأ للسيرفر
    this.logErrorToServer(error, errorInfo);
  }

  logErrorToServer = async (error, errorInfo) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.post(`${API_BASE_URL}/tech-admin/log-client-error`, {
        message: error.message,
        stack: error.stack + '\n\nComponent Stack:\n' + errorInfo.componentStack,
        url: window.location.href,
        page: window.location.pathname,
        userId: user.id,
        userEmail: user.email,
        userAgent: navigator.userAgent,
        severity: 'error'
      });
    } catch (e) {
      console.error('فشل إرسال الخطأ للسيرفر:', e);
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const t = i18n.t.bind(i18n);
      const isRTL = i18n.language?.startsWith('ar');
      return (
        <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4" dir={isRTL ? 'rtl' : 'ltr'}>
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
            <div className="text-6xl mb-4">😵</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('errorBoundary.title')}</h2>
            <p className="text-gray-600 mb-6">
              {t('errorBoundary.description')}
            </p>
            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium"
              >
                {t('errorBoundary.reload')}
              </button>
              <button
                onClick={() => window.location.href = '/'}
                className="w-full py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium"
              >
                {t('errorBoundary.goHome')}
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className={`mt-6 ${isRTL ? 'text-right' : 'text-left'} bg-red-50 p-4 rounded-lg`}>
                <summary className="cursor-pointer text-red-600 font-medium">
                  {t('errorBoundary.devDetails')}
                </summary>
                <pre className="mt-2 text-xs text-red-800 overflow-auto">
                  {this.state.error.toString()}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// وظيفة لتسجيل الأخطاء غير الملتقطة (uncaught errors)
export const initGlobalErrorHandler = () => {
  // التقاط الأخطاء غير المعالجة
  window.onerror = (message, source, lineno, colno, error) => {
    logClientError({
      message: message,
      stack: error?.stack || `at ${source}:${lineno}:${colno}`,
      severity: 'error'
    });
    return false;
  };

  // التقاط Promise rejections غير المعالجة
  window.onunhandledrejection = (event) => {
    logClientError({
      message: event.reason?.message || 'Unhandled Promise Rejection',
      stack: event.reason?.stack,
      severity: 'warning'
    });
  };
};

// وظيفة مساعدة لتسجيل الأخطاء يدوياً
export const logClientError = async (errorData) => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    await axios.post(`${API_BASE_URL}/tech-admin/log-client-error`, {
      message: errorData.message,
      stack: errorData.stack,
      url: window.location.href,
      page: window.location.pathname,
      userId: user.id,
      userEmail: user.email,
      userAgent: navigator.userAgent,
      severity: errorData.severity || 'error'
    });
  } catch (e) {
    console.error('فشل إرسال الخطأ:', e);
  }
};

export default ErrorBoundary;
